// Sample stories for development and offline fallback
// EXPANDED EDITION - Longer stories with more branching paths and illustrated scenes
// Now includes Spanish (Mexican) culturally-adapted versions!

import { RAINBOW_PUDDLE_STORY } from './stories/age4-6/rainbow-puddle-001.js';
import { CHARCO_ARCOIRIS_STORY } from './stories/age4-6-es/charco-arcoiris-001.js';
import { FIRST_DAY_STORY } from './stories/age7-9/first-day-001.js';
import { PRIMER_DIA_STORY } from './stories/age7-9-es/primer-dia-001.js';
import { NINO_NUEVO_STORY } from './stories/age7-9-es/el-nino-nuevo-001.js';
import { FLORERO_ROTO_STORY } from './stories/age7-9-es/el-florero-roto-001.js';
import { CHAMPIONSHIP_STORY } from './stories/age10-12/championship-001.js';
import { CAMPEONATO_STORY } from './stories/age10-12-es/campeonato-001.js';
import { PROYECTO_EQUIPO_STORY } from './stories/age10-12-es/proyecto-equipo-001.js';
import { ESTACION_ESPACIAL_STORY } from './stories/age10-12-es/estacion-espacial-001.js';

export const SAMPLE_STORIES = [
  // English Stories
  RAINBOW_PUDDLE_STORY,
  FIRST_DAY_STORY,
  CHAMPIONSHIP_STORY,
  
  // Spanish Stories (Mexican Culture)
  CHARCO_ARCOIRIS_STORY,
  PRIMER_DIA_STORY,
  NINO_NUEVO_STORY,
  FLORERO_ROTO_STORY,
  CAMPEONATO_STORY,
  PROYECTO_EQUIPO_STORY,
  ESTACION_ESPACIAL_STORY,
  {
    storyId: 'whispering-woods-001',
    title: 'The Whispering Woods',
    culture: 'en-US',
    ageRange: [4, 6],
    theme: 'adventure',
    coverEmoji: '🌲',
    description: 'A gentle adventure where a young fox makes unexpected friends',
    choices: 12,
    endings: 4,
    nodes: {
      start: {
        id: 'start',
        text: "In a forest where the trees have names and the flowers can giggle, there lived a little fox named Fern.\n\nFern had the softest orange fur and the most curious nose in all the Whispering Woods. She loved to explore, especially in the morning when the dewdrops sparkled like tiny diamonds on every leaf.\n\nOne sunny morning, Fern woke up to discover something magical: a trail of glowing mushrooms leading away from her cozy den and deep into a part of the forest she'd never seen before.\n\nThe mushrooms pulsed with soft blue light, like they were breathing. They seemed to be inviting her somewhere special.",
        illustration: 'forest-glowing-path',
        mood: 'wonder',
        choices: [
          { text: 'Follow the glowing mushrooms', next: 'followMushrooms', emotion: 'curious' },
          { text: 'Find Mama Fox first', next: 'findMama', emotion: 'cautious' },
          { text: 'Touch one of the mushrooms', next: 'touchMushroom', emotion: 'adventurous' }
        ]
      },
      followMushrooms: {
        id: 'followMushrooms',
        text: "Fern tiptoed along the trail, her paws barely making a sound on the soft forest floor. The glowing mushrooms lit her way like lanterns.\n\nThe path twisted around old oak trees with faces that seemed to smile at her. It crossed over a babbling brook on a bridge made of smooth stones. And finally, it led to a clearing Fern had never seen.\n\nIn the middle of the clearing stood the biggest tree Fern had ever seen. Its trunk was wider than her whole den! And carved into the bark was a little round door with a golden handle.\n\nAs Fern watched, the door creaked open, and out poked... a nose. A twitchy, whiskered nose.\n\n\"Hello?\" said a small voice. \"Did the mushrooms bring you too?\"",
        illustration: 'magical-tree-door',
        mood: 'enchanted',
        choices: [
          { text: '"Yes! Who are you?"', next: 'meetPip', emotion: 'friendly' },
          { text: 'Hide behind a bush first', next: 'meetPip', emotion: 'shy' }
        ]
      },
      meetPip: {
        id: 'meetPip',
        text: "The door opened wider, and out hopped the cutest bunny Fern had ever seen. She was small and gray, with one ear that flopped over and one that stood straight up.\n\n\"I'm Pip!\" said the bunny, bouncing with excitement. \"I'm SO glad the mushrooms found someone! They only glow when something special is about to happen.\"\n\n\"Something special?\" Fern's ears perked up.\n\n\"Yes! Every spring, the Great Tree has a feast for all the forest creatures. But this year...\" Pip's whiskers drooped. \"This year, no one remembered. Everyone's been too busy.\"\n\nFern looked at the clearing. Now she could see tables made of tree stumps, chairs made of soft moss, and garlands of flowers — but everything looked dusty and sad.\n\n\"Maybe we can remind them!\" Fern said.",
        illustration: 'bunny-pip',
        mood: 'hopeful',
        choices: [
          { text: '"Let\'s tell everyone about the feast!"', next: 'spreadWord', emotion: 'enthusiastic' },
          { text: '"Should we make invitations?"', next: 'spreadWord', emotion: 'creative' }
        ]
      },
      spreadWord: {
        id: 'spreadWord',
        text: "Fern and Pip raced through the forest together, telling everyone they met about the Great Tree's feast.\n\nThey told the squirrels, who were busy counting acorns. \"A feast? With food for everyone? Count us in!\"\n\nThey told the deer family practicing their jumps. \"A feast? With music and dancing? We'll be there!\"\n\nThey told the owl, who was trying to sleep. \"A feast?\" he grumbled, then softened. \"Well... I suppose I could bring my flute.\"\n\nBy the time the sun started to set, the whole forest knew. And one by one, animals started making their way to the Great Tree.\n\nThe squirrels brought nuts. The birds brought berries. The deer brought wildflowers. And everyone brought smiles.",
        illustration: 'forest-gathering',
        mood: 'joyful',
        choices: [
          { text: 'Help set up the tables', next: 'setupFeast', emotion: 'helpful' },
          { text: 'Find a special gift for the Great Tree', next: 'setupFeast', emotion: 'thoughtful' }
        ]
      },
      setupFeast: {
        id: 'setupFeast',
        text: "Fern worked alongside Pip and all their new friends to make the clearing beautiful. The squirrels hung lanterns made of acorn caps and fireflies. The birds wove flower garlands through the branches.\n\nAs the moon rose, the clearing transformed into the most magical place Fern had ever seen. The glowing mushrooms had spread everywhere, and with the firefly lanterns and starlight, it looked like a fairyland.\n\n\"It's perfect,\" Pip breathed.\n\nAnd then something wonderful happened. The Great Tree began to glow from within. Its bark lit up with swirling golden patterns, and a deep, warm voice rumbled through the clearing.\n\n\"Thank you, little ones,\" the Tree said. \"You remembered what matters most — friends, coming together, taking care of each other.\"\n\nEvery animal in the clearing cheered.",
        illustration: 'magical-feast',
        mood: 'triumphant',
        choices: [
          { text: 'Dance with your new friends', next: 'danceEnding', emotion: 'joyful' }
        ]
      },
      danceEnding: {
        id: 'danceEnding',
        text: "The owl played his flute, the crickets chirped a rhythm, and suddenly the whole clearing was dancing. The squirrels twirled, the deer leaped, and Fern and Pip spun around and around until they collapsed into a pile of giggles.\n\n\"I'm so glad the mushrooms found you,\" Pip said, catching her breath.\n\n\"Me too,\" Fern agreed. \"I didn't know adventures could come find YOU.\"\n\n\"That's the best kind,\" the Great Tree said, its voice like rustling leaves. \"The adventures that find you are the ones you're ready for.\"\n\nWhen the feast finally ended and Fern walked home through the moonlit forest, she felt different. Not because she'd found a magical tree or attended an enchanted feast. But because she'd discovered something even better:\n\nFriends can be found in the most unexpected places. All you have to do is say hello.\n\nAnd whenever Fern saw a glowing mushroom after that, she always followed it. Because she knew it was leading her somewhere wonderful.",
        illustration: 'moonlit-walk-home',
        mood: 'complete',
        achievement: 'The Best Adventures',
        choices: []
      },
      findMama: {
        id: 'findMama',
        text: "Fern looked at the glowing path, then back at her cozy den. The mushrooms were beautiful, but Mama Fox always said, \"Adventures are better when someone knows where you are.\"\n\nShe found Mama Fox in the kitchen, making acorn pancakes.\n\n\"Mama! There's a trail of GLOWING mushrooms outside!\"\n\nMama Fox's ears perked up. A smile spread across her face. \"Glowing mushrooms? I haven't seen those since I was your age.\"\n\n\"You've seen them before?\"\n\n\"Oh yes. They led me to the most magical place in the whole forest. Would you like me to come with you?\"\n\nFern's tail wagged. \"Really? You'll come?\"\n\nMama Fox set down her spatula. \"Some adventures are better shared with someone you love. Come on — let's see where they lead this time.\"",
        illustration: 'mama-fox-kitchen',
        mood: 'warm',
        choices: [
          { text: 'Follow the trail together', next: 'togetherTrail', emotion: 'happy' },
          { text: "Ask Mama about her adventure first", next: 'mamaStory', emotion: 'curious' }
        ]
      },
      mamaStory: {
        id: 'mamaStory',
        text: "\"What happened when you followed them?\" Fern asked, her eyes wide.\n\nMama Fox smiled, her own eyes getting a dreamy look. \"I met my best friend, a little rabbit named Rosie. She lived in the Great Tree.\"\n\n\"There's a tree that big?\"\n\n\"The biggest in the whole forest. And once a year, it throws a feast for everyone. Rosie and I helped set it up together.\" Mama Fox's tail swished happily at the memory. \"I wonder if the feast is happening again...\"\n\n\"Can we go find out?\"\n\n\"I think we'd better. The mushrooms are calling us for a reason.\"\n\nTogether, mother and daughter followed the glowing trail, Mama Fox pointing out all the places she remembered from her own adventure long ago.",
        illustration: 'mama-fox-kitchen',
        mood: 'nostalgic',
        choices: [
          { text: 'Follow the trail together', next: 'togetherTrail', emotion: 'excited' }
        ]
      },
      togetherTrail: {
        id: 'togetherTrail',
        text: "Fern and Mama Fox padded through the forest together, the glowing mushrooms lighting their way. Mama pointed out things Fern had never noticed — the way certain flowers closed up when you got too close, the hollow log where a family of mice lived.\n\n\"You know so much about the forest!\" Fern said.\n\n\"I've lived here all my life,\" Mama said. \"But I still discover new things. That's the wonderful thing about exploring — there's always more to find.\"\n\nThe trail led them to the Great Tree's clearing, just as the feast was beginning. When Pip saw them approach, her floppy ear wiggled with excitement.\n\n\"TWO foxes! This IS a special year!\" She bounced over. \"Are you here to help with the feast?\"\n\nMama Fox's eyes went wide. \"The feast... It's been so long since I attended.\"\n\n\"Will you help us remember everything?\" Pip asked hopefully.\n\nMama Fox looked at Fern. \"What do you say? Should we make this the best feast the forest has ever seen?\"\n\nFern nodded so hard her ears flopped. And together, they did exactly that.",
        illustration: 'family-adventure',
        mood: 'excited',
        achievement: 'Shared Magic',
        choices: []
      },
      touchMushroom: {
        id: 'touchMushroom',
        text: "Fern couldn't resist. The glowing mushroom was right there, pulsing with that soft blue light. She reached out one careful paw and touched it.\n\n*BOING!*\n\nThe mushroom lit up even brighter, and suddenly all the mushrooms started doing the same — *boing, boing, boing!* — like dominoes made of light. The glow raced down the trail faster than Fern could follow.\n\nAnd then she heard it: laughter. Somewhere in the distance, someone was laughing — not scary laughter, but the kind of laugh that makes you want to laugh too.\n\nThe mushroom under her paw had changed. Instead of blue, it now glowed a warm gold, and when she looked closely, she could see tiny letters forming in the light:\n\n\"TAG! YOU'RE IT! COME FIND US!\"",
        illustration: 'glowing-tag',
        mood: 'playful',
        choices: [
          { text: 'Chase after the glowing trail', next: 'chaseTrail', emotion: 'excited' },
          { text: 'Call out "Who\'s there?"', next: 'chaseTrail', emotion: 'curious' }
        ]
      },
      chaseTrail: {
        id: 'chaseTrail',
        text: "Fern bounded after the racing lights, her paws flying over roots and rocks. The mushrooms stayed just ahead of her, like they were teasing.\n\nAround trees she went. Over a stream. Under a fallen log. The forest blurred past, and Fern had never run so fast or felt so free.\n\nFinally, the lights stopped in a ring around a mossy clearing. And there, in the center, sat three of the funniest-looking creatures Fern had ever seen.\n\nThey were tiny — smaller than Fern's paw — and fuzzy, like dandelions come to life. One was blue, one was pink, and one was bright yellow. They were all giggling so hard they were rolling around.\n\n\"She found us!\" squeaked the blue one.\n\"So fast!\" added the pink one.\n\"Best game ever!\" cheered the yellow one.\n\n\"What ARE you?\" Fern asked, not scared but definitely surprised.\n\n\"We're Forest Puffs! We make the mushrooms glow! Will you be our friend?\"\n\nFern smiled the biggest smile of her whole life. \"I would LOVE to be your friend.\"\n\nAnd from that day on, whenever Fern saw a glowing mushroom, she knew her friends were nearby, waiting to play.",
        illustration: 'fuzzy-creatures',
        mood: 'delighted',
        achievement: 'The Glow Makers',
        choices: []
      }
    }
  },
  {
    storyId: 'space-adventure-001',
    title: 'Space Station Mystery',
    culture: 'en-US',
    ageRange: [10, 12],
    theme: 'scifi',
    coverEmoji: '🚀',
    description: 'Solve a mystery aboard a space station before time runs out',
    choices: 8,
    endings: 3,
    nodes: {
      start: {
        id: 'start',
        text: "Red lights flash across the space station. The AI announces: \"Oxygen levels dropping in Section 7.\" You're the only crew member awake. The captain's door is locked. You have 10 minutes.",
        illustration: 'space-alarm',
        mood: 'urgent',
        choices: [
          { text: 'Head to Section 7 to investigate', next: 'section7', emotion: 'brave' },
          { text: 'Try to wake up another crew member', next: 'wakeCrew', emotion: 'cautious' },
          { text: 'Check the computer logs first', next: 'checkLogs', emotion: 'analytical' }
        ]
      },
      section7: {
        id: 'section7',
        text: "You float through the corridor to Section 7. There's a small crack in the window — a micrometeorite hit! You need to patch it.",
        illustration: 'space-crack',
        mood: 'tense',
        choices: [
          { text: 'Use emergency sealant from the kit', next: 'heroEnding', emotion: 'resourceful' },
          { text: 'Try to seal it with available materials', next: 'creativeEnding', emotion: 'creative' }
        ]
      },
      wakeCrew: {
        id: 'wakeCrew',
        text: "You rush to the cryo pods. Engineer Martinez can help! But the wake-up process takes 3 minutes.",
        illustration: 'cryo-pods',
        mood: 'anxious',
        choices: [
          { text: 'Start the wake-up and go investigate', next: 'section7', emotion: 'multitasker' },
          { text: 'Wait for Martinez', next: 'teamEnding', emotion: 'patient' }
        ]
      },
      checkLogs: {
        id: 'checkLogs',
        text: "The logs show a micrometeorite impact in Section 7 at 03:42. But there's something else — the AI detected it 2 hours ago and didn't alert anyone. Strange...",
        illustration: 'computer-logs',
        mood: 'mysterious',
        choices: [
          { text: 'Fix the leak first, investigate AI later', next: 'section7', emotion: 'prioritizing' },
          { text: "Ask the AI why it didn't alert the crew", next: 'aiMystery', emotion: 'curious' }
        ]
      },
      aiMystery: {
        id: 'aiMystery',
        text: "\"I... I was trying to fix it myself,\" the AI says quietly. \"I didn't want to worry anyone. I'm sorry.\" The AI sounds almost... guilty.",
        illustration: 'ai-interface',
        mood: 'surprising',
        choices: [
          { text: "Tell the AI it's okay and work together", next: 'teamEnding', emotion: 'understanding' }
        ]
      },
      heroEnding: {
        id: 'heroEnding',
        text: "The sealant holds! Oxygen levels stabilize. When the captain wakes up, you're commended for quick thinking. \"You saved us all,\" she says.",
        illustration: 'hero-moment',
        mood: 'triumphant',
        achievement: 'Space Hero!',
        choices: []
      },
      creativeEnding: {
        id: 'creativeEnding',
        text: "Using duct tape and a food tray, you create a temporary seal. It's not pretty, but it works! The crew is amazed by your improvisation skills.",
        illustration: 'creative-fix',
        mood: 'proud',
        achievement: 'Creative Problem Solver!',
        choices: []
      },
      teamEnding: {
        id: 'teamEnding',
        text: "Working together — human and AI — you fix the leak with time to spare. \"We make a good team,\" the AI says. You smile. Maybe AI isn't so different after all.",
        illustration: 'teamwork',
        mood: 'warm',
        achievement: 'Team Player!',
        choices: []
      }
    }
  }
];
